import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import classnames from 'classnames';
import Sidebar from 'react-sidebar';
import { useSelector } from 'react-redux';
import _ from 'lodash';

import Loader from '../../common/loader/Loader';
import SidebarContent from '../../common/sidebar/SidebarContent';

import CourseLevelIcon from '../../common/icons/CourseLevelIcon';
import WebinarIcon from '../../common/icons/WebinarIcon';
import EuroIcon from '../../common/icons/EuroIcon';

import FooterMobile from '../common/footer/FooterMobile';

import MobileHeader from '../common/header/MobileHeader';

import ParticipateForm from '../../common/forms/ParticipateForm';

import { getCourseColor } from '../../../utils/course-utils';

import { getMobileSidebarStyles } from '../../../utils/sidebar-utils';

const useCourseInfoPageState = courseId => {
  const [courseData, setCourseData] = useState(null);

  useEffect(() => {
    axios.get(`/api/v1/courses/${courseId}`).then(response => {
      const data = response.data.course;

      const webinarsCount = _.sumBy(data.sections, x => x.webinars.length);

      setCourseData({ ...data, webinarsCount });
    });
  }, []);

  return { courseData };
};

const CourseInfoLabelMobile = props => {
  return (
    <div className="m-course-mobile-label">
      <div className="m-course-mobile-label-line" />
      <div className="py-5px">{props.icon}</div>
      <p className="m-course-mobile-label-text">{props.text}</p>
    </div>
  );
};

CourseInfoLabelMobile.propTypes = {
  icon: PropTypes.node.isRequired,
  text: PropTypes.string.isRequired,
};

const CourseSectionMobile = props => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleIsOpen = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="m-course-section">
      <div className="m-course-section-header" onClick={toggleIsOpen}>
        <div className="is-flex align-items-center justify-content-between">
          <div>
            <div className="is-flex align-items-center">
              <p className="m-course-section-num">SECTION {props.index}</p>
              <div className="ml-15px" style={{ marginTop: '2px' }}>
                <WebinarIcon color={getCourseColor(props.type)} />
              </div>
              <p className={classnames('m-course-section-webinar ml-10px', props.colorClassNames)}>
                {props.section.webinars.length} webinars
              </p>
            </div>
            <p className="m-course-section-name mt-15px">{props.section.name}</p>
          </div>
          <div>
            <img src="img/icons/caret-down.svg" />
          </div>
        </div>
      </div>
      {isOpen && (
        <div className="m-course-content">
          <ol>
            {props.section.webinars.map((webinar, webinarIndex) => (
              <li key={webinarIndex}>
                <span className="ml-15px">{webinar.name}</span>
                <ul className="course-content-ul">
                  {webinar.paragraphs.map((paragraph, paragraphIndex) => (
                    <li key={paragraphIndex}>{paragraph}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

CourseSectionMobile.propTypes = {
  index: PropTypes.number.isRequired,
  type: PropTypes.number.isRequired,
  colorClassNames: PropTypes.string.isRequired,
  section: PropTypes.shape({
    name: PropTypes.string.isRequired,
    webinars: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        paragraphs: PropTypes.arrayOf(PropTypes.string),
      }),
    ).isRequired,
  }).isRequired,
};

const getLevelText = level => {
  if (level === 1) {
    return 'beginner';
  }

  if (level === 2) {
    return 'intermediate';
  }

  return 'advanced';
};

const CourseInfoPageMobile = props => {
  const { courseData } = useCourseInfoPageState(props.match.params.courseId);

  const { isAuthenticated } = useSelector(state => state);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleSidebarOpen = e => {
    e.preventDefault();
    setIsMenuOpen(!isMenuOpen);
  };

  const closeSidebar = () => {
    setIsMenuOpen(false);
  };

  if (!courseData) {
    return <Loader isVisible={true} />;
  }

  const courseInfoClassNames = classnames('m-course-info', {
    'm-course-csharp': courseData.type === 1,
    'm-course-javascript': courseData.type === 2,
    'm-course-typescript': courseData.type === 3,
    'm-course-nodejs': courseData.type === 4,
  });

  const colorClassNames = classnames({
    'is-csharp': courseData.type === 1,
    'is-javascript': courseData.type === 2,
    'is-typescript': courseData.type === 3,
    'is-nodejs': courseData.type === 4,
  });

  const btnBgClassName = classnames({
    'is-csharp-bg': courseData.type === 1,
    'is-javascript-bg': courseData.type === 2,
    'is-typescript-bg': courseData.type === 3,
    'is-nodejs-bg': courseData.type === 4,
  });

  return (
    <Sidebar
      open={isMenuOpen}
      onSetOpen={setIsMenuOpen}
      sidebar={<SidebarContent closeSidebar={closeSidebar} />}
      styles={getMobileSidebarStyles()}
    >
      <div className={courseInfoClassNames}>
        <MobileHeader isAuthenticated={isAuthenticated} toggleSidebarOpen={toggleSidebarOpen} isTransparent />
        <div className="mx-15px py-40px">
          <p className="m-course-title">{courseData.name}</p>
          <p className="m-course-description mt-25px">{courseData.description}</p>
          <div className="is-flex justify-content-between mt-25px">
            <CourseInfoLabelMobile
              text={`${courseData.duration} months`}
              icon={<img src="img/icons/calendar.svg" className="is-block" width={18} />}
            />
            <CourseInfoLabelMobile
              text={`${courseData.webinarsCount} webinars`}
              icon={<img src="img/icons/webinar-white.svg" className="is-block" width={18} />}
            />
            <CourseInfoLabelMobile
              text={`${courseData.tasksCount} tasks`}
              icon={<img src="img/icons/checkbox-checked.svg" className="is-block" width={18} />}
            />
            <CourseInfoLabelMobile
              text={`Level: ${getLevelText(courseData.level)}`}
              icon={<CourseLevelIcon level={courseData.level} />}
            />
          </div>
        </div>
      </div>
      <div className="m-course-program">
        <p className="m-course-info-title">Course program</p>
        <div className="m-course-sections mt-20px">
          {courseData.sections.map((section, sectionIndex) => (
            <CourseSectionMobile
              key={section.name}
              index={sectionIndex + 1}
              section={section}
              colorClassNames={colorClassNames}
              type={courseData.type}
            />
          ))}
        </div>
      </div>
      <div className="m-price-block">
        <p className="m-course-info-title">Leave a request to register for this course</p>
        <div className="m-price-block-content mt-25px">
          <p className="m-price-course-name">{courseData.name}</p>
          <div className="is-flex align-items-center my-10px">
            <CourseLevelIcon level={courseData.level} type={courseData.type} isColorful={true} />
            <span className="m-course-price-text ml-10px">Level: {getLevelText(courseData.level)}</span>
          </div>
          <div style={{ display: 'inline-block' }} className="mt-20px">
            <div className="m-old-price-block">
              <span>{courseData.oldPrice}</span>
              <EuroIcon color="black" size={14} />
              <div className="m-old-price-strikethrough" />
            </div>
          </div>
          <div className="is-flex align-items-center">
            <p className={classnames('m-new-price-block', colorClassNames)}>
              {courseData.price}
              <EuroIcon color={getCourseColor(courseData.type)} />
            </p>
            <div className="ml-20px">
              <p className="m-course-price-text">unique webinars</p>
              <p className="m-course-price-text">individual tasks</p>
            </div>
          </div>
        </div>
        <div className="mt-15px" style={{ width: '100%' }}>
          <ParticipateForm
            btnClassName={classnames('submit-button submit-course-button', btnBgClassName)}
            courseId={courseData._id}
            isMobile
          />
        </div>
      </div>
      <FooterMobile />
    </Sidebar>
  );
};

CourseInfoPageMobile.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      courseId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default CourseInfoPageMobile;
